import React, { useCallback, useMemo, useRef, useState } from "react";
import TableContainer from "components/common/TableContainer";
import { Link } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import StatusBadge from "components/common/StatusBadge";
import {
  CodeCell,
  IndexColumn,
  SourceColumn,
  resolveMappedSourceCell,
} from "components/common/columns";
import { DateOnly, DateTime } from "components/common/Date";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { Payment } from "features/Payments/types/payment.types";
import ConfirmActionModal from "components/common/ConfirmActionModal";
import {
  cancelPayment,
  downloadPaymentReceipt,
  getPayments,
} from "features/Payments/services/PaymentService";
import {
  resolvePaymentPayeeName,
  resolvePaymentSourceCode,
  resolvePaymentSourceLinkId,
} from "features/Payments/utils/paymentSource";
import {
  PaymentSourceLabelMap,
  PaymentStatus,
  paymentStatusMap,
} from "features/Payments/constants/payment.constants";
import { getClientDisplayName } from "features/Clients/utils/clientDisplayName";
import PaymentAmountBadge from "../PaymentAmountBadge";
import { withLoadingToast } from "utils/withLoadingToast";
import { toast } from "react-toastify";
import PaymentListFilters from "./PaymentListFilters";

function paymentErrorMessage(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { message?: string } } })?.response
    ?.data?.message;
  return typeof msg === "string" && msg.trim() ? msg : fallback;
}

export const PaymentsTable = () => {
  const tableRef = useRef<() => void>(null);
  const { can } = useAccess();
  const canReadPayment = can(Permissions.PAYMENT.READ);
  const canCancelPayment = can(Permissions.PAYMENT.CANCEL);
  const [cancelTarget, setCancelTarget] = useState<Payment | null>(null);
  const [status, setStatus] = useState("");

  const requestParams = useMemo(() => ({ status: status || undefined }), [status]);
  const filterCount = [status].filter(Boolean).length;

  const onDownloadReceipt = useCallback(async (p: Payment) => {
    await withLoadingToast(
      "Preparing receipt…",
      () => downloadPaymentReceipt(p.id, p.code),
      "Receipt downloaded",
      "Could not download receipt",
      (e) => (e instanceof Error ? e.message : "Could not download receipt"),
    );
  }, []);

  const onConfirmCancelPayment = useCallback(async () => {
    if (!cancelTarget) return;
    const id = cancelTarget.id;
    const toastId = toast.loading("Cancelling payment…");
    try {
      const res = await cancelPayment(id);
      if (!res.success) {
        throw new Error(res.message || "Could not cancel payment");
      }
      toast.update(toastId, {
        render: "Payment cancelled",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      setCancelTarget(null);
      tableRef.current?.();
    } catch (err) {
      toast.update(toastId, {
        render: paymentErrorMessage(err, "Could not cancel payment"),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  }, [cancelTarget]);

  const columns: ColumnDef<Payment>[] = useMemo(
    () => [
      IndexColumn<Payment>(),
      {
        header: "Code",
        cell: ({ row }) => (
          <Link
            to={`/finance/payments/${row.original.id}`}
            className="text-decoration-none link"
          >
            <CodeCell code={row.original.code} />
          </Link>
        ),
        accessorKey: "code",
        enableColumnFilter: false,
      },
      SourceColumn<Payment>({
        id: "linkedRecord",
        resolve: (row) =>
          resolveMappedSourceCell(
            row.sourceType,
            resolvePaymentSourceLinkId(row),
            {
              typeLabelMap: PaymentSourceLabelMap,
              source: { code: resolvePaymentSourceCode(row) },
            },
          ),
      }),
      {
        header: "Client",
        id: "client",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const { client, clientId } = row.original;
          if (!client) return <span className="text-muted">—</span>;
          return (
            <Link
              to={`/clients/${clientId}`}
              className="link text-body fw-medium text-truncate d-inline-block"
              style={{ maxWidth: 180 }}
              title={getClientDisplayName(client)}
            >
              {getClientDisplayName(client)}
            </Link>
          );
        },
      },
      {
        header: "Paid to",
        id: "payee",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const { payee, payeeId } = row.original;
          if (!payee || !payeeId) return <span className="text-muted">—</span>;
          const name = resolvePaymentPayeeName(row.original);
          return (
            <Link
              to={`/employees/${payeeId}`}
              className="link text-body fw-medium text-truncate d-inline-block"
              style={{ maxWidth: 180 }}
              title={name ?? undefined}
            >
              {name ?? `#${payeeId}`}
            </Link>
          );
        },
      },
      {
        header: "Paid via",
        accessorFn: (row) => row.modeOfPayment?.name ?? "",
        id: "modeOfPayment",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const name = row.original.modeOfPayment?.name;
          return name ? (
            <span className="text-body">{name}</span>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        header: "Amount",
        accessorKey: "amount",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return <PaymentAmountBadge record={row.original} />;
        },
      },
      {
        header: "Payment date",
        accessorKey: "paymentDate",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const d = row.original.paymentDate;
          return d ? (
            <DateOnly date={d} />
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return (
            <StatusBadge value={row.original.status} map={paymentStatusMap} />
          );
        },
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return <DateTime date={row.original.created_at} />;
        },
      },

      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Payment actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                <DropdownItem tag={Link} to={`/finance/payments/${record.id}`}>
                  <i className="ri-eye-line align-bottom me-2 text-muted" />
                  View
                </DropdownItem>
                {canReadPayment ? (
                  <DropdownItem
                    tag="button"
                    type="button"
                    disabled={record.status !== PaymentStatus.POSTED}
                    title={
                      record.status !== PaymentStatus.POSTED
                        ? "Receipt is only available for posted payments"
                        : undefined
                    }
                    onClick={() => void onDownloadReceipt(record)}
                  >
                    <i className="ri-file-pdf-2-line align-bottom me-2 text-muted" />
                    Download receipt
                  </DropdownItem>
                ) : null}
                {canCancelPayment ? (
                  <DropdownItem
                    tag="button"
                    type="button"
                    className="text-danger"
                    disabled={record.status !== PaymentStatus.POSTED}
                    title={
                      record.status !== PaymentStatus.POSTED
                        ? "Only posted payments can be cancelled"
                        : undefined
                    }
                    onClick={() => setCancelTarget(record)}
                  >
                    <i className="ri-close-circle-line align-bottom me-2" />
                    Cancel payment
                  </DropdownItem>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [canReadPayment, canCancelPayment, onDownloadReceipt],
  );

  return (
    <React.Fragment>
      <ConfirmActionModal
        show={cancelTarget != null}
        onClose={() => setCancelTarget(null)}
        title={cancelTarget ? cancelTarget.code : undefined}
        iconClass="ri-close-circle-line"
        iconColor="text-danger"
        heading="Cancel this payment?"
        description={
          cancelTarget
            ? `This will void payment ${cancelTarget.code} and update linked records (e.g. invoice balance). This action may not be reversible.`
            : undefined
        }
        confirmText="Cancel payment"
        confirmColor="danger"
        cancelText="Keep payment"
        onConfirm={() => void onConfirmCancelPayment()}
      />
      <TableContainer
        columns={columns}
        fetchApi={getPayments}
        requestParams={requestParams}
        pageSize={10}
        globalSearch={true}
        SearchPlaceholder="Search by code, source, client…"
        refreshRef={tableRef}
        divClass="table-responsive table-card mb-3"
        tableClass="align-middle table-nowrap mb-0"
        theadClass="table-light text-muted"
        filterCount={filterCount}
        onFiltersClear={() => setStatus("")}
        toolbarRight={
          <PaymentListFilters status={status} onStatusChange={setStatus} />
        }
      />
    </React.Fragment>
  );
};
